import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  Input,
  inject,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../auth/services/auth.service';
import { DashboardLeadsStatusComponent } from '../dashboard-leads-status/dashboard-leads-status.component';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { AddBusinessRecordComponent } from '../../../search/components/add-business-record/add-business-record.component';
import { LeadsService } from '../../../leads/services/leads.service';
import { DashboardMetrics } from '../../models/dashboard-metrics';

type DashboardTabId = 'lead-status' | 'deeproots' | 'lead-criteria' | 'links';

interface DashboardTab {
  id: DashboardTabId;
  label: string;
}

interface CountSummary {
  total: number;
  items: { label: string; count: number }[];
  percentageTotal?: number;
  itemMetaLabel?: string;
}

interface DeepRootsChartItem {
  label: string;
  inquiries: number;
  matches: number;
}

interface LinksChartItem {
  label: string;
  submissions: number;
}

interface CriteriaPieSlice {
  index: number;
  label: string;
  count: number;
  percent: number;
  color: string;
  path: string;
  lineStartX: number;
  lineStartY: number;
  lineBreakX: number;
  lineBreakY: number;
  lineEndX: number;
  lineEndY: number;
  textX: number;
  textY: number;
  textAnchor: 'start' | 'end';
}

const CRITERIA_CHART_COLORS = [
  '#4f46e5',
  '#0284c7',
  '#059669',
  '#d97706',
  '#7c3aed',
  '#0d9488',
  '#e11d48',
  '#475569',
];

@Component({
  selector: 'app-dashboard-deeproots-tabs',
  standalone: true,
  imports: [
    CommonModule,
    DashboardLeadsStatusComponent,
    AddBusinessRecordComponent,
  ],
  templateUrl: './dashboard-deeproots-tabs.component.html',
  styleUrl: './dashboard-deeproots-tabs.component.scss',
})
export class DashboardDeeprootsTabsComponent
  implements OnInit, OnChanges, OnDestroy
{
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly leadsService = inject(LeadsService);
  private readonly criteriaChartColors = CRITERIA_CHART_COLORS;

  @Input() dashboardMetrics: DashboardMetrics | null = null;
  @Input() isLoading = false;
  @Output() refreshMetrics = new EventEmitter<void>();
  @Output() leadsToastMessage = new EventEmitter<{
    message: string;
    type: 'success' | 'error';
  }>();

  onRefreshMetrics(): void {
    this.refreshMetrics.emit();
  }

  readonly hasLeadStatusAccess = this.authService.hasPermission(
    PermissionCode.GET_SET_LEADS,
  );
  readonly hasDeepRootsAccess = this.authService.hasPermission(
    PermissionCode.ACCESS_DEEPROOTS,
  );
  readonly canManageCriteria = this.authService.hasPermission(
    PermissionCode.MANAGE_MATCHING_CRITERIA,
  );
  readonly canManageLinks = this.authService.hasPermission(
    PermissionCode.MANAGE_PUBLIC_LINKS,
  );
  readonly canAddLead = this.authService.hasPermission(
    PermissionCode.CUSTOM_BUSINESS_RECORD,
  );

  matchedRecordsSummary: CountSummary = {
    total: 0,
    items: [],
    percentageTotal: 0,
    itemMetaLabel: 'of total matches',
  };
  inquiriesSummary: CountSummary = {
    total: 0,
    items: [],
    percentageTotal: 0,
    itemMetaLabel: 'of total inquiries',
  };
  leadCriteriaSummary: CountSummary = {
    total: 0,
    items: [],
    percentageTotal: 0,
    itemMetaLabel: 'of criteria matches',
  };
  linksSummary: CountSummary = {
    total: 0,
    items: [],
    percentageTotal: 0,
    itemMetaLabel: 'of total inquiries',
  };
  linksChartItems: LinksChartItem[] = [];
  linksChartMaxValue = 0;
  linksChartAxisMax = 0;
  linksChartAxisTicks: number[] = [];
  linksChartAxisTicksDesc: number[] = [];
  isLinksChartAnimated = false;
  totalInquiriesReceived = 0;
  deepRootsChartItems: DeepRootsChartItem[] = [];
  deepRootsChartMaxValue = 0;
  deepRootsChartAxisMax = 0;
  deepRootsChartAxisTicks: number[] = [];
  deepRootsChartAxisTicksDesc: number[] = [];
  isDeepRootsBarsAnimated = false;
  isCriteriaChartAnimated = false;

  activeTab: DashboardTabId | null = null;
  isDashboardExpanded = true;
  private deepRootsAnimationFrameId: number | null = null;
  private criteriaAnimationFrameId: number | null = null;
  private linksAnimationFrameId: number | null = null;

  get tabs(): DashboardTab[] {
    const tabs: DashboardTab[] = [];

    if (this.hasLeadStatusAccess) {
      tabs.push({ id: 'lead-status', label: 'Overview' });
    }

    if (this.hasDeepRootsAccess) {
      tabs.push({ id: 'deeproots', label: 'DeepRoots' });
    }

    if (this.hasDeepRootsAccess || this.canManageCriteria) {
      tabs.push({ id: 'lead-criteria', label: 'Criteria' });
    }

    if (this.hasDeepRootsAccess || this.canManageLinks) {
      tabs.push({ id: 'links', label: 'Links' });
    }

    return tabs;
  }

  get totalAssignedLeads(): number {
    if (!this.dashboardMetrics) return 0;
    if (
      this.dashboardMetrics.summary?.assigned_lead_count !== undefined &&
      this.dashboardMetrics.summary?.assigned_lead_count !== null
    ) {
      return this.dashboardMetrics.summary.assigned_lead_count;
    }
    return (
      this.dashboardMetrics.lead_count_per_status?.reduce(
        (sum, item) => sum + item.count,
        0,
      ) ?? 0
    );
  }

  get lastRefreshedAtDate(): string | null {
    const raw = this.dashboardMetrics?.last_refreshed_at;
    if (!raw) return null;
    let formatted = raw.trim();
    if (formatted.includes(' ') && !formatted.includes('T')) {
      formatted = formatted.replace(' ', 'T');
    }
    if (!formatted.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(formatted)) {
      formatted = `${formatted}Z`;
    }
    return formatted;
  }

  private readonly STORAGE_KEY = 'dashboard_expanded_sections';

  ngOnInit(): void {
    this.loadExpandedState();
    this.activeTab = this.tabs[0]?.id ?? null;
    if (this.activeTab === 'deeproots') {
      this.triggerDeepRootsBarAnimation();
    }
    if (this.activeTab === 'lead-criteria') {
      this.triggerCriteriaChartAnimation();
    }
    if (this.activeTab === 'links') {
      this.triggerLinksChartAnimation();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dashboardMetrics'] && this.dashboardMetrics) {
      this.updateSummaries();
      if (this.activeTab === 'deeproots') {
        this.triggerDeepRootsBarAnimation();
      }
      if (this.activeTab === 'lead-criteria') {
        this.triggerCriteriaChartAnimation();
      }
      if (this.activeTab === 'links') {
        this.triggerLinksChartAnimation();
      }
    }
  }

  private updateSummaries(): void {
    if (!this.dashboardMetrics) return;

    // Store total inquiries received
    this.totalInquiriesReceived =
      this.dashboardMetrics.summary.total_inquiries_received;

    // Update matched records summary from inquiries_matched_per_product
    this.matchedRecordsSummary = {
      total: this.dashboardMetrics.summary.total_inquiries_matched,
      items: this.dashboardMetrics.inquiries_matched_per_product
        .map((item) => ({
          label: item.label,
          count: item.count,
        }))
        .sort((a, b) => b.count - a.count),
      percentageTotal: this.dashboardMetrics.summary.total_inquiries_matched,
      itemMetaLabel: 'of total matches',
    };

    // Update inquiries summary from inquiries_received_per_product
    this.inquiriesSummary = {
      total: this.dashboardMetrics.summary.total_inquiries_received,
      items: this.dashboardMetrics.inquiries_received_per_product
        .map((item) => ({
          label: item.label,
          count: item.count,
        }))
        .sort((a, b) => b.count - a.count),
      percentageTotal: this.dashboardMetrics.summary.total_inquiries_received,
      itemMetaLabel: 'of total inquiries',
    };

    // Update lead criteria summary from inquiries_matched_per_criteria
    this.leadCriteriaSummary = {
      total: this.dashboardMetrics.summary.total_criteria_sets,
      items: this.dashboardMetrics.inquiries_matched_per_criteria
        .map((item) => ({
          label: item.label,
          count: item.count,
        }))
        .sort((a, b) => b.count - a.count),
      percentageTotal: this.dashboardMetrics.summary.total_inquiries_matched,
      itemMetaLabel: 'of total matches',
    };

    // Update links summary from submissions_received_per_link
    this.linksSummary = {
      total: this.dashboardMetrics.summary.total_public_links,
      items: this.dashboardMetrics.submissions_received_per_link
        .map((item) => ({
          label: item.label,
          count: item.count,
        }))
        .sort((a, b) => b.count - a.count),
      percentageTotal: this.totalInquiriesReceived,
      itemMetaLabel: 'of total inquiries',
    };

    this.updateDeepRootsChartItems();
    this.updateLinksChartItems();
  }

  private updateDeepRootsChartItems(): void {
    const countsByLabel = new Map<
      string,
      { inquiries: number; matches: number }
    >();

    this.inquiriesSummary.items.forEach((item) => {
      countsByLabel.set(item.label, {
        inquiries: item.count,
        matches: 0,
      });
    });

    this.matchedRecordsSummary.items.forEach((item) => {
      const existing = countsByLabel.get(item.label) ?? {
        inquiries: 0,
        matches: 0,
      };
      countsByLabel.set(item.label, {
        ...existing,
        matches: item.count,
      });
    });

    this.deepRootsChartItems = Array.from(countsByLabel.entries())
      .map(([label, counts]) => ({
        label,
        inquiries: counts.inquiries,
        matches: counts.matches,
      }))
      .filter((item) => {
        // Exclude "Unspecified" from the chart
        if (item.label.toLowerCase() === 'unspecified') {
          return false;
        }
        return item.inquiries > 0 || item.matches > 0;
      })
      .sort((a, b) => b.inquiries - a.inquiries || b.matches - a.matches);

    this.deepRootsChartMaxValue = this.deepRootsChartItems.reduce(
      (max, item) => Math.max(max, item.inquiries, item.matches),
      0,
    );

    this.deepRootsChartAxisMax = this.getNiceAxisMax(
      this.deepRootsChartMaxValue,
    );
    this.deepRootsChartAxisTicks = this.buildAxisTicks(
      this.deepRootsChartAxisMax,
    );
    this.deepRootsChartAxisTicksDesc = [
      ...this.deepRootsChartAxisTicks,
    ].reverse();
  }

  private getNiceAxisMax(value: number): number {
    if (value <= 0) return 1;

    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    const normalized = value / magnitude;
    let roundedNormalized = 10;

    if (normalized <= 1) {
      roundedNormalized = 1;
    } else if (normalized <= 2) {
      roundedNormalized = 2;
    } else if (normalized <= 5) {
      roundedNormalized = 5;
    }

    return roundedNormalized * magnitude;
  }

  private buildAxisTicks(axisMax: number): number[] {
    const segments = 5;
    const step = axisMax / segments;
    return Array.from({ length: segments + 1 }, (_, index) =>
      Math.round(index * step),
    );
  }

  selectTab(tabId: DashboardTabId): void {
    this.activeTab = tabId;

    if (tabId === 'deeproots') {
      this.triggerDeepRootsBarAnimation();
      this.isCriteriaChartAnimated = false;
      this.isLinksChartAnimated = false;
    } else if (tabId === 'lead-criteria') {
      this.triggerCriteriaChartAnimation();
      this.isDeepRootsBarsAnimated = false;
      this.isLinksChartAnimated = false;
    } else if (tabId === 'links') {
      this.triggerLinksChartAnimation();
      this.isDeepRootsBarsAnimated = false;
      this.isCriteriaChartAnimated = false;
    } else {
      this.isDeepRootsBarsAnimated = false;
      this.isCriteriaChartAnimated = false;
      this.isLinksChartAnimated = false;
    }
  }

  private triggerDeepRootsBarAnimation(): void {
    this.isDeepRootsBarsAnimated = false;

    if (this.deepRootsAnimationFrameId !== null) {
      cancelAnimationFrame(this.deepRootsAnimationFrameId);
    }

    this.deepRootsAnimationFrameId = requestAnimationFrame(() => {
      this.deepRootsAnimationFrameId = requestAnimationFrame(() => {
        this.isDeepRootsBarsAnimated = true;
        this.deepRootsAnimationFrameId = null;
      });
    });
  }

  private triggerCriteriaChartAnimation(): void {
    this.isCriteriaChartAnimated = false;

    if (this.criteriaAnimationFrameId !== null) {
      cancelAnimationFrame(this.criteriaAnimationFrameId);
    }

    this.criteriaAnimationFrameId = requestAnimationFrame(() => {
      this.criteriaAnimationFrameId = requestAnimationFrame(() => {
        this.isCriteriaChartAnimated = true;
        this.criteriaAnimationFrameId = null;

        // Sprout confetti burst directly from high-match cards
        setTimeout(() => {
          const highMatchCards = document.querySelectorAll(
            '.criteria-card.high-match',
          );
          highMatchCards.forEach((card, idx) => {
            setTimeout(() => {
              this.triggerConfettiForElement(card as HTMLElement);
            }, idx * 80);
          });
        }, 250);
      });
    });
  }

  onCriteriaCardClick(
    event: Event,
    item: { label: string; count: number },
  ): void {
    const pct = this.getSummaryItemPercentage(
      this.leadCriteriaSummary,
      item.count,
    );
    if (pct >= 90) {
      const target = event.currentTarget as HTMLElement;
      const cardEl =
        (target?.closest('.criteria-card') as HTMLElement) ?? target;
      if (cardEl) {
        this.triggerConfettiForElement(cardEl);
      }
    }
  }

  triggerConfettiForElement(element: HTMLElement): void {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      !element
    ) {
      return;
    }

    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '99999';
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      canvas.remove();
      return;
    }

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = [
      '#2563eb',
      '#06b6d4',
      '#22c55e',
      '#eab308',
      '#f97316',
      '#ef4444',
      '#8b5cf6',
      '#ec4899',
    ];

    const originX = rect.left + rect.width / 2;
    const originY = rect.top + rect.height * 0.35;

    const particleCount = 10;
    const particles = Array.from({ length: particleCount }).map(() => ({
      x: originX + (Math.random() - 0.5) * (rect.width * 0.4),
      y: originY + (Math.random() - 0.5) * (rect.height * 0.2),
      w: Math.random() * 3 + 2,
      h: Math.random() * 3 + 2,
      vx: (Math.random() - 0.5) * 3,
      vy: -(Math.random() * 3 + 1.5),
      rotation: Math.random() * 360,
      vRot: (Math.random() - 0.5) * 8,
      color: colors[Math.floor(Math.random() * colors.length)] ?? '#2563eb',
      opacity: 1,
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
    }));

    const startTime = Date.now();
    const duration = 850;

    const render = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= duration) {
        canvas.remove();
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.18;
        p.vx *= 0.95;
        p.rotation += p.vRot;

        if (elapsed > duration - 300) {
          p.opacity = Math.max(0, (duration - elapsed) / 300);
        }

        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;

        if (p.shape === 'rect') {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      });

      requestAnimationFrame(render);
    };

    requestAnimationFrame(render);
  }

  navigateTo(path: string): void {
    const [route, queryString] = path.split('?');
    const queryParams: Record<string, string> = {};

    if (queryString) {
      queryString.split('&').forEach((param) => {
        const [key, value] = param.split('=');
        if (key && value) {
          queryParams[key] = value;
        }
      });
      this.router.navigate([route], { queryParams });
    } else {
      this.router.navigate([route]);
    }
  }

  onLeadsToastMessage(event: { message: string; type: 'success' | 'error' }) {
    this.leadsToastMessage.emit(event);
  }

  onLeadAdded(message: string): void {
    this.leadsService.triggerStatusCountRefresh();
    this.leadsToastMessage.emit({ message, type: 'success' });
  }

  trackByLabel(_: number, item: { label: string; count: number }): string {
    return item.label;
  }

  getSummaryItemPercentage(summary: CountSummary, count: number): number {
    const total = summary.percentageTotal ?? summary.total;
    return total > 0 ? Math.round((count / total) * 100) : 0;
  }

  getDeepRootsBarHeight(count: number): number {
    return this.deepRootsChartAxisMax > 0
      ? (count / this.deepRootsChartAxisMax) * 100
      : 0;
  }

  getCriteriaColor(_index: number, count?: number): string {
    if (count === undefined || count === null || count === 0) {
      return '#cbd5e1';
    }

    const pct = this.getSummaryItemPercentage(this.leadCriteriaSummary, count);

    if (pct >= 75) {
      return '#10b981'; // High match: Emerald Green
    }
    if (pct >= 50) {
      return '#84cc16'; // Good match: Lime Green
    }
    if (pct >= 25) {
      return '#f59e0b'; // Moderate match: Amber Yellow
    }
    return '#ef4444'; // Low match: Soft Red
  }

  getLeadCriteriaPieSlices(): CriteriaPieSlice[] {
    const total = this.leadCriteriaSummary.percentageTotal ?? 0;

    if (total <= 0 || this.leadCriteriaSummary.items.length === 0) {
      return [];
    }

    let startAngle = -90;

    return this.leadCriteriaSummary.items.map((item, index) => {
      const percent = this.getSummaryItemPercentage(
        this.leadCriteriaSummary,
        item.count,
      );
      const sweep = (item.count / total) * 360;
      const endAngle = startAngle + sweep;
      const path = this.describeDonutSlicePath(startAngle, endAngle);
      const midAngle = startAngle + sweep / 2;
      const lineStart = this.polarToCartesian(50, 50, 49, midAngle);
      const lineBreak = this.polarToCartesian(50, 50, 53, midAngle);
      const rightSide = lineBreak.x >= 50;
      const lineEndX = rightSide ? lineBreak.x + 3 : lineBreak.x - 3;
      const textX = rightSide ? lineEndX + 1 : lineEndX - 1;

      const slice: CriteriaPieSlice = {
        index,
        label: item.label,
        count: item.count,
        percent,
        color: this.getCriteriaColor(index),
        path,
        lineStartX: lineStart.x,
        lineStartY: lineStart.y,
        lineBreakX: lineBreak.x,
        lineBreakY: lineBreak.y,
        lineEndX,
        lineEndY: lineBreak.y,
        textX,
        textY: lineBreak.y,
        textAnchor: rightSide ? 'start' : 'end',
      };

      startAngle = endAngle;
      return slice;
    });
  }

  getLeadCriteriaAxisTicksDesc(): number[] {
    const axisMax = this.getNiceAxisMax(this.getLeadCriteriaMaxCount());
    return this.buildAxisTicks(axisMax).reverse();
  }

  getLeadCriteriaBarHeight(count: number): number {
    const axisMax = this.getNiceAxisMax(this.getLeadCriteriaMaxCount());
    return axisMax > 0 ? (count / axisMax) * 100 : 0;
  }

  private getLeadCriteriaMaxCount(): number {
    const max = this.leadCriteriaSummary.items.reduce(
      (currentMax, item) => Math.max(currentMax, item.count),
      0,
    );
    return max;
  }

  private describeDonutSlicePath(startAngle: number, endAngle: number): string {
    const centerX = 50;
    const centerY = 50;
    const outerRadius = 48;
    const innerRadius = 20;

    const outerStart = this.polarToCartesian(
      centerX,
      centerY,
      outerRadius,
      startAngle,
    );
    const outerEnd = this.polarToCartesian(
      centerX,
      centerY,
      outerRadius,
      endAngle,
    );
    const innerEnd = this.polarToCartesian(
      centerX,
      centerY,
      innerRadius,
      endAngle,
    );
    const innerStart = this.polarToCartesian(
      centerX,
      centerY,
      innerRadius,
      startAngle,
    );
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return [
      `M ${outerStart.x} ${outerStart.y}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}`,
      `L ${innerEnd.x} ${innerEnd.y}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
      'Z',
    ].join(' ');
  }

  private polarToCartesian(
    centerX: number,
    centerY: number,
    radius: number,
    angleInDegrees: number,
  ): { x: number; y: number } {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;

    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  }

  toggleDashboard(): void {
    this.isDashboardExpanded = !this.isDashboardExpanded;
    this.saveExpandedState();
  }

  private loadExpandedState(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored !== null) {
        const parsed = JSON.parse(stored);
        if (typeof parsed?.leadsDashboard === 'boolean') {
          this.isDashboardExpanded = parsed.leadsDashboard;
        } else if (typeof parsed?.leadsStatus === 'boolean') {
          this.isDashboardExpanded = parsed.leadsStatus;
        } else if (typeof parsed?.leadStatus === 'boolean') {
          this.isDashboardExpanded = parsed.leadStatus;
        }
      } else {
        const legacyStored = localStorage.getItem(
          'dashboard-leads-status-expanded',
        );
        if (legacyStored !== null) {
          this.isDashboardExpanded = legacyStored === 'true';
          localStorage.removeItem('dashboard-leads-status-expanded');
          this.saveExpandedState();
        }
      }
    } catch {
      this.isDashboardExpanded = true;
    }
  }

  private saveExpandedState(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : {};
      parsed.leadsDashboard = this.isDashboardExpanded;
      parsed.leadsStatus = this.isDashboardExpanded;
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // Ignore storage errors
    }
  }

  private updateLinksChartItems(): void {
    // Show all links individually
    const sortedItems = [...this.linksSummary.items].sort(
      (a, b) => b.count - a.count,
    );

    this.linksChartItems = sortedItems.map((item) => ({
      label: item.label,
      submissions: item.count,
    }));

    // Calculate total submissions from all links
    const totalSubmissions = this.linksChartItems.reduce(
      (sum, item) => sum + item.submissions,
      0,
    );

    // Add "Others" bar representing inquiries not captured by any link
    const othersCount = Math.max(
      0,
      this.totalInquiriesReceived - totalSubmissions,
    );
    if (othersCount > 0 || this.linksChartItems.length > 0) {
      this.linksChartItems.push({
        label: 'Others',
        submissions: othersCount,
      });
    }

    // Y-axis should go up to total inquiries
    this.linksChartAxisMax = this.totalInquiriesReceived;
    this.linksChartAxisTicks = this.buildAxisTicks(this.linksChartAxisMax);
    this.linksChartAxisTicksDesc = [...this.linksChartAxisTicks].reverse();
  }

  getLinksBarHeight(count: number): number {
    // Use total inquiries as the denominator for height calculation
    return this.totalInquiriesReceived > 0
      ? (count / this.totalInquiriesReceived) * 100
      : 0;
  }

  private triggerLinksChartAnimation(): void {
    this.isLinksChartAnimated = false;

    if (this.linksAnimationFrameId !== null) {
      cancelAnimationFrame(this.linksAnimationFrameId);
    }

    this.linksAnimationFrameId = requestAnimationFrame(() => {
      this.linksAnimationFrameId = requestAnimationFrame(() => {
        this.isLinksChartAnimated = true;
        this.linksAnimationFrameId = null;
      });
    });
  }

  ngOnDestroy(): void {
    if (this.deepRootsAnimationFrameId !== null) {
      cancelAnimationFrame(this.deepRootsAnimationFrameId);
    }
    if (this.criteriaAnimationFrameId !== null) {
      cancelAnimationFrame(this.criteriaAnimationFrameId);
    }
    if (this.linksAnimationFrameId !== null) {
      cancelAnimationFrame(this.linksAnimationFrameId);
    }
  }
}
